import Api from '@/api'
import Project from '@/models/Project'

const buildSharedProjectsQuery = (params = {}, direction = null) => {
  const query = new URLSearchParams()
  query.set('page', params.page || 1)
  query.set('limit', params.limit || 10)

  if (direction) {
    query.set('direction', direction)
  }
  if (params.include) {
    query.set('include', params.include)
  }
  if (params.sort) {
    query.set('sort', params.sort)
  }
  if (params.projectId) {
    query.set('filter[project_id]', params.projectId)
  }
  if (params.search) {
    query.set('search', params.search)
  }

  return query.toString()
}

// Dedupes concurrent getProjects calls with identical params (e.g. multiple
// timesheet tabs mounting at once) so they share one request/commit instead
// of racing clearProjects/setProjects against each other.
const projectsInFlight = {}

export default {
  namespaced: true,

  state: {
    projects: [],
    projectsMeta: null,
    // true when the last getProjects call failed, so the UI can distinguish an
    // empty result ("no projects assigned") from a failed fetch.
    projectsError: false,
    archivedProjects: [],
    project: null,

    tradies: [],
    tradiesMeta: null,
    tradie: null,

    nTradies: [],
    nTradiesMeta: null,

    files: [],
    requiredFiles: [],
    requestedFiles: [],

    templateOptions: [],

    sharedToMeProjects: [],
    sharedToMeProjectsMeta: null,
    sharedByMeProjects: [],
    sharedByMeProjectsMeta: null,
  },

  getters: {
    // Projects
    projects(state) {
      return state.projects
    },
    projectsMeta(state) {
      return state.projectsMeta
    },
    project(state) {
      return state.project
    },

    // Shared Projects
    sharedToMeProjects(state) {
      return state.sharedToMeProjects
    },

    sharedToMeProjectsMeta(state) {
      return state.sharedToMeProjectsMeta
    },

    sharedByMeProjects(state) {
      return state.sharedByMeProjects
    },

    sharedByMeProjectsMeta(state) {
      return state.sharedByMeProjectsMeta
    },

    // Tradies
    tradies(state) {
      return state.tradies
    },
    tradiesMeta(state) {
      return state.tradiesMeta
    },
    tradie(state) {
      return state.tradie
    },

    // Non Reponsive Tradies
    nTradies(state) {
      return state.nTradies
    },
    nTradiesMeta(state) {
      return state.nTradiesMeta
    },

    // Tradie's Files
    files(state) {
      return state.files
    },
    requiredFiles(state) {
      return state.requiredFiles
    },
    requestedFiles(state) {
      return state.requestedFiles
    },

    templateOptions(state) {
      return state.templateOptions
    },
  },

  mutations: {
    setProjects(state, projects) {
      state.projects = projects
    },
    setProjectsMeta(state, projectsMeta) {
      state.projectsMeta = projectsMeta
    },
    clearProjects(state) {
      state.projects = []
    },
    setProjectsError(state, value) {
      state.projectsError = value
    },
    setArchivedProjects(state, projects) {
      state.archivedProjects = projects
    },

    setProject(state, project) {
      state.project = project
    },
    clearProject(state) {
      state.project = null
    },

    setTradies(state, tradies) {
      state.tradies = tradies
    },
    setTradiesMeta(state, tradiesMeta) {
      state.tradiesMeta = tradiesMeta
    },
    clearTradies(state) {
      state.tradies = []
    },

    setTradie(state, tradie) {
      state.tradie = tradie
    },
    clearTradie(state) {
      state.tradie = null
    },

    setNTradies(state, nTradies) {
      state.nTradies = nTradies
    },
    setNTradiesMeta(state, nTradiesMeta) {
      state.nTradiesMeta = nTradiesMeta
    },
    clearNTradies(state) {
      state.nTradies = []
    },

    setFiles(state, files) {
      state.files = files
    },
    clearFiles(state) {
      state.files = []
    },
    updateFileCompanyApproval(state, payload) {
      if (!payload || !Array.isArray(state.files)) return
      const idx = state.files.findIndex((f) => f && f.id === payload.id)
      if (idx === -1) return
      const merged = {
        ...state.files[idx],
        company_approval: payload.company_approval,
        company_approval_reason: payload.company_approval_reason,
        company_approval_at: payload.company_approval_at,
        company_approval_by: payload.company_approval_by,
      }
      state.files.splice(idx, 1, merged)
    },

    setRequiredFiles(state, requiredFiles) {
      state.requiredFiles = requiredFiles
    },
    clearRequiredFiles(state) {
      state.requiredFiles = []
    },

    setRequestedFiles(state, requestedFiles) {
      state.requestedFiles = requestedFiles
    },
    clearRequestedFiles(state) {
      state.requestedFiles = []
    },

    setTemplateOptions(state, templateOptions) {
      state.templateOptions = templateOptions
    },

    setSharedToMeProjects(state, sharedProjects) {
      state.sharedToMeProjects = sharedProjects
    },

    setSharedToMeProjectsMeta(state, sharedProjectsMeta) {
      state.sharedToMeProjectsMeta = sharedProjectsMeta
    },

    clearSharedToMeProjects(state) {
      state.sharedToMeProjects = []
    },

    clearSharedToMeProjectsMeta(state) {
      state.sharedToMeProjectsMeta = null
    },

    setSharedByMeProjects(state, sharedProjects) {
      state.sharedByMeProjects = sharedProjects
    },

    setSharedByMeProjectsMeta(state, sharedProjectsMeta) {
      state.sharedByMeProjectsMeta = sharedProjectsMeta
    },

    clearSharedByMeProjects(state) {
      state.sharedByMeProjects = []
    },

    clearSharedByMeProjectsMeta(state) {
      state.sharedByMeProjectsMeta = null
    },
  },

  actions: {
    async getProjects({ commit }, params) {
      const key = JSON.stringify(params || {})
      if (projectsInFlight[key]) return projectsInFlight[key]

      const request = (async () => {
        commit('clearProjects')
        commit('setProjectsError', false)

        const query = Project.page(params.page || 1).limit(params.limit || 10)
        query.where('search', params.search || null)
        if (params.is_archived !== undefined && params.is_archived !== null) {
          query.where('is_archived', params.is_archived ? 1 : 0)
        }

        try {
          const res = await query.get()
          commit('setProjects', res.data)
          commit('setProjectsMeta', res.meta)
        } catch (e) {
          // Flag the failure so the UI can show a load error instead of a blank
          // dropdown, then rethrow so existing callers' handling is unchanged.
          commit('setProjectsError', true)
          throw e
        }
      })()

      projectsInFlight[key] = request
      try {
        await request
      } finally {
        delete projectsInFlight[key]
      }
    },

    async getArchivedProjects({ commit }) {
      const query = Project.page(1).limit(200)
      query.where('is_archived', 1)
      const res = await query.get()
      commit('setArchivedProjects', res.data || [])
    },

    async getProject({ commit }, payload) {
      commit('clearProject')
      commit('clearTradies')
      commit('clearNTradies')
      commit('clearTradie')
      commit('clearFiles')
      commit('clearRequiredFiles')
      commit('clearRequestedFiles')

      await new Promise(async (resolve, reject) => {
        await Api.get(
          `projects/${payload.id}?limit=${payload.limit}&page=${payload.page}`,
        )
          .then((res) => {
            if (res.data.data) {
              commit('setProject', res.data.data)
              commit('setTradies', res.data.data.tradies.data)
              commit('setTradiesMeta', res.data.data.tradies.meta)
              resolve(res.data.data)
            }
          })
          .catch((err) => reject(err.response))
      })
    },

    async createProject({ commit }, project) {
      await new Promise(async (resolve, reject) => {
        await Api.post('/projects', project)
          .then((res) => resolve(res.data.data))
          .catch((err) => reject(err.response))
      })
    },

    async updateProject({ commit }, project) {
      await new Promise(async (resolve, reject) => {
        await Api.patch(`projects/${project.id}`, project)
          .then((res) => {
            commit('setProject', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async deleteProject({ commit }, id) {
      try {
        const res = await Api.delete(`projects/${id}`)
        commit('clearProject')
        return res.data
      } catch (err) {
        throw err.response
      }
    },

    async getSharedToMeProjects({ commit }, params = {}) {
      commit('clearSharedToMeProjects')
      commit('clearSharedToMeProjectsMeta')
      const query = buildSharedProjectsQuery(params, 'incoming')
      const { data } = await Api.get(`/project-shares?${query}`)
      if (data) {
        commit('setSharedToMeProjects', data.data || [])
        commit('setSharedToMeProjectsMeta', data.meta || null)
      }
    },

    async getSharedByMeProjects({ commit }, params = {}) {
      commit('clearSharedByMeProjects')
      commit('clearSharedByMeProjectsMeta')
      const query = buildSharedProjectsQuery(params, 'outgoing')
      const { data } = await Api.get(`/project-shares?${query}`)
      if (data) {
        commit('setSharedByMeProjects', data.data || [])
        commit('setSharedByMeProjectsMeta', data.meta || null)
      }
    },

    async getProjectShare(_ctx, shareId) {
      const { data } = await Api.get(`/project-shares/${shareId}`)
      return (data && data.data) || data || null
    },

    // The project documents (project files) a share includes.
    async getProjectShareFiles(_ctx, shareId) {
      const { data } = await Api.get(`/project-shares/${shareId}/files`)
      return (data && data.data) || { file_ids: [], files: [] }
    },

    async updateProjectShareFiles(_ctx, { shareId, fileIds }) {
      const { data } = await Api.put(`/project-shares/${shareId}/files`, {
        file_ids: fileIds,
      })
      return (data && data.data) || { file_ids: [], files: [] }
    },

    async createProjectShare(_ctx, payload) {
      const { data } = await Api.post('/project-shares', payload)
      return data
    },

    async deleteProjectShare(_ctx, projectShareId) {
      await Api.delete(`/project-shares/${projectShareId}`)
    },

    async deleteReceivedProjectShare(_ctx, projectShareId) {
      await Api.delete(`/project-shares/${projectShareId}/received`)
    },

    async getTradiesByProjectId({ commit }, payload) {
      commit('clearTradies')

      await new Promise(async (resolve, reject) => {
        await Api.get(
          `projects/${payload.projectId}?limit=${payload.limit}&page=${
            payload.page
          }${payload.search ? `&search=${payload.search}` : ''}`,
        )
          .then((res) => {
            if (res.data.data) {
              commit('setTradies', res.data.data.tradies.data)
              commit('setTradiesMeta', res.data.data.tradies.meta)
              resolve(res.data.data)
            }
          })
          .catch((err) => reject(err.response))
      })
    },

    async getProjectTradies(_ctx, projectId) {
      const res = await Api.get(`projects/${projectId}?limit=100&page=1`)
      return (
        (res.data &&
          res.data.data &&
          res.data.data.tradies &&
          res.data.data.tradies.data) ||
        []
      )
    },

    // Batched sibling of getProjectTradies: the lightweight roster (id/
    // full_name/trade) for every project the caller can see, in one request,
    // instead of calling GET /projects/{id} (which also computes per-tradie
    // file-compliance counts nobody here needs) once per project.
    async getAllProjectTradies() {
      const res = await Api.get('projects/tradies-batch')
      return (res.data && res.data.data) || []
    },

    async getNonRespondingTradies({ commit }, payload) {
      commit('clearNTradies')

      await new Promise(async (resolve, reject) => {
        await Api.get(
          `projects/${payload.projectId}/non-responding-tradies?limit=${
            payload.limit
          }&page=${payload.page}${
            payload.search ? `&search=${payload.search}` : ''
          }`,
        )
          .then((res) => {
            if (res.data.data) {
              commit('setNTradies', res.data.data)
              // meta sits alongside data on the response, not nested under
              // it — every other paginated action in this store reads
              // res.data.meta the same way; this one alone read
              // res.data.data.meta (undefined, since res.data.data is the
              // items array), so rows2/nTradiesMeta.total was always 0.
              commit('setNTradiesMeta', res.data.meta)
              resolve(res.data.data)
            }
          })
          .catch((err) => reject(err.response))
      })
    },

    async getTemplateOptions({ commit }) {
      await new Promise(async (resolve, reject) => {
        await Api.get('templates/getTemplateOptions')
          .then((res) => {
            commit('setTemplateOptions', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async addTemplate({ commit }, data) {
      await new Promise(async (resolve, reject) => {
        Api.post(`projects/${data.id}/addRequiredFileTemplates`, {
          template_ids: data.templateIds,
        })
          .then((res) => {
            commit('setProject', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async removeTemplate({ commit }, data) {
      await new Promise(async (resolve, reject) => {
        Api.post(`projects/${data.id}/removeRequiredFileTemplate`, {
          template_id: data.templateId,
        })
          .then((res) => {
            commit('setProject', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async addTradies({ commit }, data) {
      await new Promise(async (resolve, reject) => {
        Api.post(`projects/${data.id}/add-tradies`, {
          tradie_ids: data.tradieIds,
        })
          .then((res) => {
            commit('setProject', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async importTradieCSV({ commit }, data) {
      const formData = new FormData()
      formData.append('csv_file', data.tradieCsvFile)

      return new Promise((resolve, reject) => {
        Api.post(`projects/${data.projectId}/import-tradie-csv`, formData)
          .then((res) => resolve(res.data.data))
          .catch((err) => reject(err.response))
      })
    },

    async removeTradie({ commit }, data) {
      await new Promise(async (resolve, reject) => {
        Api.post(`projects/${data.id}/remove-tradie`, {
          tradie_id: data.tradieId,
        })
          .then((res) => {
            commit('setProject', res.data.data)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async getTradie({ commit }, data) {
      commit('clearTradie')
      commit('clearFiles')
      commit('clearRequiredFiles')
      commit('clearRequestedFiles')

      await new Promise(async (resolve, reject) => {
        Api.get(`projects/${data.id}/tradie/${data.tradieId}`)
          .then((res) => {
            commit('setTradie', res.data.data)
            commit('setFiles', res.data.files)
            commit('setRequiredFiles', res.data.required_files)
            commit('setRequestedFiles', res.data.requested_files)
            resolve(res.data.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async addTradesToTradie({ commit }, data) {
      await new Promise(async (resolve, reject) => {
        Api.post(`projects/${data.id}/tradie/${data.tradieId}/add-trades`, {
          trade_ids: data.tradeIds,
        })
          .then((res) => {
            commit('setTradie', res.data.data)
            commit('setFiles', res.data.files)
            commit('setRequiredFiles', res.data.required_files)
            commit('setRequestedFiles', res.data.requested_files)
            resolve(res.data.data)
          })
          .catch((err) => {
            reject(err.response)
          })
      })
    },

    async removeTradeFromTradie({ commit }, data) {
      await new Promise(async (resolve, reject) => {
        const payload = {
          trade_id: data.tradeId,
          trade_ids: [data.tradeId],
        }
        Api.post(
          `projects/${data.id}/tradie/${data.tradieId}/remove-trade`,
          payload,
        )
          .then((res) => {
            const tradie =
              res.data.data || res.data.tradie || res.data.tradie_data
            if (tradie) {
              commit('setTradie', tradie)
            }
            if (res.data.files !== undefined) {
              commit('setFiles', res.data.files || [])
            }
            if (res.data.required_files !== undefined) {
              commit('setRequiredFiles', res.data.required_files || [])
            }
            if (res.data.requested_files !== undefined) {
              commit('setRequestedFiles', res.data.requested_files || [])
            }
            resolve(tradie)
          })
          .catch((err) => reject(err.response))
      })
    },

    async deleteFile({ commit, state }, id) {
      await new Promise(async (resolve, reject) => {
        await Api.delete(`file/${id}`)
          .then((res) => {
            commit('setFiles', [...state.files.filter((x) => x.id !== id)])
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },
  },
}
